import { useState, useMemo, useCallback } from "react";
import moment from "moment";
import { ACCEPTED_CREDIT_CARDS } from "../../constants";

const LAST4_PATTERN = /^\d{4}$/;
const TICKET_ID_PATTERN = /^[a-zA-Z0-9]{12}$/;
const LICENSE_PLATE_PATTERN = /^[a-zA-Z0-9]{3,10}$/;

const VALID_CARD_TYPES = Object.entries(ACCEPTED_CREDIT_CARDS)
  .filter(([key]) => key !== "Unset")
  .map(([, value]) => value);

const formatCentsAsAmount = (cents) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

const isPaymentDateValid = (value) => moment.isMoment(value) && value.isValid();

const isCardTypeValid = (value) => VALID_CARD_TYPES.includes(value);

const isLast4Valid = (value) => LAST4_PATTERN.test(value);

const isTicketIdValid = (value) => {
  const trimmed = value.trim();
  return trimmed === "" || TICKET_ID_PATTERN.test(trimmed);
};

const isLicensePlateValid = (value) => {
  const trimmed = value.trim();
  return trimmed === "" || LICENSE_PLATE_PATTERN.test(trimmed);
};

/**
 * Form state and validation for the public Receipt Lookup portal.
 *
 * Holds field values, per-field touched/blur state, derived per-field validity,
 * a derived `isFormValid` that gates submission, and the amount cents-accumulator.
 *
 * @returns {object} Field values, change/blur handlers, the error map, validity
 *   flags, `isFormValid`, and `buildPayload` for assembling the flat submit object.
 */
const useReceiptLookupForm = () => {
  const [paymentDate, setPaymentDate] = useState(moment());
  const [cardType, setCardType] = useState(ACCEPTED_CREDIT_CARDS.Unset);
  const [last4, setLast4] = useState("");
  const [amountCents, setAmountCents] = useState(0);
  const [ticketId, setTicketId] = useState("");
  const [licensePlate, setLicensePlate] = useState("");

  const [touched, setTouched] = useState({});

  const amountDisplay = useMemo(
    () => formatCentsAsAmount(amountCents),
    [amountCents]
  );
  const amount = useMemo(() => amountCents / 100, [amountCents]);

  const validity = useMemo(
    () => ({
      paymentDate: isPaymentDateValid(paymentDate),
      cardType: isCardTypeValid(cardType),
      last4: isLast4Valid(last4),
      amount: amountCents > 0,
      ticketId: isTicketIdValid(ticketId),
      licensePlate: isLicensePlateValid(licensePlate),
    }),
    [paymentDate, cardType, last4, amountCents, ticketId, licensePlate]
  );

  const isFormValid =
    validity.paymentDate &&
    validity.cardType &&
    validity.last4 &&
    validity.amount &&
    validity.ticketId &&
    validity.licensePlate;

  const errors = useMemo(
    () => ({
      paymentDate:
        touched.paymentDate && !validity.paymentDate
          ? "Enter a valid payment date."
          : "",
      cardType:
        touched.cardType && !validity.cardType
          ? "Select a credit card type."
          : "",
      last4:
        touched.last4 && !validity.last4
          ? "Enter exactly 4 digits."
          : "",
      amount:
        touched.amount && !validity.amount ? "Enter an amount." : "",
      ticketId:
        touched.ticketId && !validity.ticketId
          ? "Ticket ID must be 12 letters or numbers."
          : "",
      licensePlate:
        touched.licensePlate && !validity.licensePlate
          ? "License plate must be 3 to 10 letters or numbers."
          : "",
    }),
    [touched, validity]
  );

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const handlePaymentDateChange = useCallback((value) => {
    setPaymentDate(value);
  }, []);

  const handleCardTypeChange = useCallback((value) => {
    setCardType(value);
  }, []);

  const handleLast4Change = useCallback((value) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 4);
    setLast4(digitsOnly);
  }, []);

  const handleAmountChange = useCallback((value) => {
    const digitsOnly = value.replace(/\D/g, "");
    setAmountCents(digitsOnly === "" ? 0 : parseInt(digitsOnly, 10));
  }, []);

  const handleTicketIdChange = useCallback((value) => {
    setTicketId(value);
  }, []);

  const handleLicensePlateChange = useCallback((value) => {
    setLicensePlate(value);
  }, []);

  const buildPayload = useCallback(
    (entityId) => {
      const trimmedTicketId = ticketId.trim();
      const trimmedLicensePlate = licensePlate.trim();

      const payload = {
        entityId,
        paymentDate: paymentDate.toISOString(),
        cardType,
        last4,
        amount,
      };

      if (trimmedTicketId !== "") {
        payload.ticketId = trimmedTicketId;
      }
      if (trimmedLicensePlate !== "") {
        payload.licensePlate = trimmedLicensePlate;
      }

      return payload;
    },
    [ticketId, licensePlate, paymentDate, cardType, last4, amount]
  );

  return {
    values: {
      paymentDate,
      cardType,
      last4,
      amount,
      amountDisplay,
      ticketId,
      licensePlate,
    },
    errors,
    validity,
    isFormValid,
    handleBlur,
    handlePaymentDateChange,
    handleCardTypeChange,
    handleLast4Change,
    handleAmountChange,
    handleTicketIdChange,
    handleLicensePlateChange,
    buildPayload,
  };
};

export default useReceiptLookupForm;
